/**
 * Profile model, the post-run update, gear operations and versioned save (de)serialisation
 * (PLAN §11). Pure functions: every update returns a new Profile and never mutates its input.
 * No Date: runs are ordered by runIndex. No Math.random: loot uses a passed Rng (or
 * `profileRng`, which derives one from the profile seed and run index).
 */
import type { BotSkillPreset, ClassDef, ClassId, Rng, Role, RunResult } from '@keystone/sim';
import { createSeededRng } from '@keystone/sim';
import { DEFAULT_PARTY, PLAYABLE_CLASSES, RETIRED_CLASS_TO_BASE, getContentBundle } from '@keystone/content';
import { CLASS_STAT_PROFILES, MAX_GEM_RANK, legendary } from './data';
import { compileCharacter } from './compile';
import { ROLE_GEM_COLOR, addGem, autoGem, fuse, fuseGoldCost, setSocket, takeGem } from './gems';
import type { FuseResult } from './gems';
import { itemScore, starterGear, validateItem } from './items';
import { MAX_EQUIPPED_LEGENDARIES } from './items.legendaries';
import { trackVault } from './loot';
import { hasUnlock, scoreRun, tierDef, tierFor, totalRating, updateRecord } from './rating';
import { defaultLoadout, fillLoadout, talentNode, validateLoadout } from './talents';
import { addXp } from './leveling';
import { REFORGE_GOLD, reforge, upgrade } from './upgrade';
import { BLESSED_CAP, REFORGE_FANGSTEEL, gradeForKey, gradeIndex } from './forge.constants';
import { emptyItemForge, gradeForCraftIlvl, legacySalvageYield, salvageGems } from './forge.items';
import { addBundles, credit, emptySatchel, newForgeState, sanitizeBundle, starterWallet, zeroScales } from './forge.wallet';
import { dayNumber, isLocalDate } from './forge.dates';
import { bracketClear, dailyOffering, dungeonBossCount, rollRunResources, satchelRecipients, vaultOffer, vaultOptionBundle, weeklyAfterRun, weeklyReady, worldFindsToBundle } from './payout';
import { runRewards } from './rewards';
import { newHeroCharacter } from './hero';
import type { KillReward, RunRewards } from './rewards';
import type {
  CharacterProgress,
  CompiledCharacter,
  EquippedGear,
  Gem,
  GemColor,
  Item,
  ItemId,
  ItemSlot,
  Profile,
  RankStanding,
  ResourceBundle,
  RunLoot,
  RunPayout,
  RunRecord,
  ScaleGrade,
  SecondaryStat,
  VaultOptionKind,
} from './types';
import { ITEM_SLOTS, PROFILE_VERSION, SCALE_GRADES } from './types';
import { TOWN_POINT } from '@keystone/content';
import { withSpec } from './specs';
import { gridPoints, sanitizeGrid } from './grid'; // +grid

const BUNDLE_CLASSES = getContentBundle().classes;
/** +classes: the playable base classes (character creation, Group Finder), tank and healer first. */
export const PROFILE_CLASSES: readonly ClassId[] = [...PLAYABLE_CLASSES];
/** The retired M+ kits: `newPartyProfile` (legacy harness / tools) gives one character per class. */
export const LEGACY_PROFILE_CLASSES: readonly ClassId[] = [...DEFAULT_PARTY];

export function roleOf(classId: ClassId): Role {
  return BUNDLE_CLASSES.find((c) => c.id === classId)?.role ?? 'dps';
}

function allocId(p: Profile, prefix: string): string {
  const id = `${prefix}${p.nextId}`;
  p.nextId += 1;
  return id;
}

/**
 * Deterministic RNG for a profile event: mixes the profile seed with a salt (use the run index
 * for loot, so replaying the same run on the same profile gives the same loot).
 */
export function profileRng(p: Pick<Profile, 'seed'>, salt: number): Rng {
  let h = (p.seed ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (salt >>> 0), 0x85ebca6b) >>> 0;
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  h ^= h >>> 16;
  return createSeededRng(h >>> 0);
}

function newCharacter(p: Profile, classId: ClassId): CharacterProgress {
  const priority = CLASS_STAT_PROFILES[classId]?.priority ?? ['haste', 'crit', 'expertise', 'spirit'];
  const items = starterGear(classId, priority, () => allocId(p, 'i'));
  p.inventory.push(...items);
  const gear: CharacterProgress['gear'] = {};
  for (const it of items) gear[it.slot] = it.id;
  const loadout = defaultLoadout(roleOf(classId), 1, tierDef('novice').talentTiers, classId);
  return {
    classId,
    hero: { level: 1, xp: 0, talentPoints: 1, specId: null },
    gear,
    loadouts: [loadout],
    activeLoadoutId: loadout.id,
    autoManaged: true,
    satchel: emptySatchel(),
    grid: { nodes: [], locks: [], keyLog: [] }, // +grid
  };
}

/**
 * +field: add a class's progress (level 1, starter gear ilvl 200 in the inventory, recommended
 * talents) to `p` in place; returns it. createCharacter uses it for the one character.
 */
export function addCharacterProgress(p: Profile, classId: ClassId): CharacterProgress {
  const ch = newCharacter(p, classId);
  p.characters[classId] = ch;
  return ch;
}

/**
 * A fresh v5 profile: no character yet (`character: null`, `characters: {}`); `mainClassId` is the
 * class preselected on the character creation screen. createCharacter (field.ts) adds the one hero.
 */
export function newProfile(mainClassId: ClassId, seed: number): Profile {
  return {
    version: PROFILE_VERSION,
    nextId: 1,
    seed: seed >>> 0,
    mainClassId,
    character: null,
    characters: {},
    inventory: [],
    gems: {},
    currencies: { shards: 0, gold: 0 },
    vault: { runsInWindow: 0, windowLevels: [], pending: null, offer: null },
    runs: [],
    rating: {},
    settings: { version: 1, defaultBotPreset: 'competent', shareSigils: null },
    wallet: starterWallet(),
    forge: newForgeState(),
    legacy: null,
  };
}

/**
 * A profile with a character per class (starter gear; the main human-managed, the rest auto-managed
 * companions): the pre-v5 M+ party model. For tools and tests (harness progression sim, forge and
 * autopilot tests); the game itself has one character (newProfile + createCharacter).
 */
export function newPartyProfile(mainClassId: ClassId, seed: number): Profile {
  const p: Profile = {
    version: PROFILE_VERSION,
    nextId: 1,
    seed: seed >>> 0,
    mainClassId,
    character: null,
    characters: {},
    inventory: [],
    gems: {},
    currencies: { shards: 0, gold: 0 },
    vault: { runsInWindow: 0, windowLevels: [], pending: null, offer: null },
    runs: [],
    rating: {},
    settings: { version: 1, defaultBotPreset: 'competent', shareSigils: null },
    wallet: starterWallet(),
    forge: newForgeState(),
    legacy: null,
  };
  const classes = LEGACY_PROFILE_CLASSES.includes(mainClassId) ? LEGACY_PROFILE_CLASSES : [...LEGACY_PROFILE_CLASSES, mainClassId];
  for (const c of classes) p.characters[c] = newCharacter(p, c);
  const main = p.characters[mainClassId];
  if (main) main.autoManaged = false;
  return p;
}

// ============================================================================
// Queries
// ============================================================================

export function itemById(p: Profile, id: string | undefined): Item | undefined {
  return id === undefined ? undefined : p.inventory.find((i) => i.id === id);
}

/** The class that has this item equipped, if any. */
export function itemOwner(p: Profile, itemId: ItemId): ClassId | null {
  for (const id of Object.keys(p.characters).sort()) {
    const ch = p.characters[id]!;
    if (ITEM_SLOTS.some((s) => ch.gear[s] === itemId)) return id;
  }
  return null;
}

export function equippedGear(p: Profile, classId: ClassId): EquippedGear {
  const ch = p.characters[classId];
  const out: EquippedGear = {};
  if (!ch) return out;
  for (const s of ITEM_SLOTS) {
    const it = itemById(p, ch.gear[s]);
    if (it) out[s] = it;
  }
  return out;
}

/** Items no character has equipped (the bag). */
export function unequippedItems(p: Profile): Item[] {
  const used = new Set<string>();
  for (const ch of Object.values(p.characters)) for (const s of ITEM_SLOTS) if (ch.gear[s]) used.add(ch.gear[s]!);
  return p.inventory.filter((i) => !used.has(i.id));
}

export function profileRating(p: Profile): number {
  return totalRating(p.rating);
}

export function profileStanding(p: Profile): RankStanding {
  return tierFor(profileRating(p));
}

/** Highest gem rank this profile may fuse (0 until gems unlock at Contender 3). */
export function profileMaxGemRank(p: Profile): number {
  const st = profileStanding(p);
  return st.unlocks.includes('gems') ? Math.min(MAX_GEM_RANK, tierDef(st.tier).maxGemRank) : 0;
}

/** Compile a profile character against its base ClassDef (from the content bundle). */
export function compileProfileCharacter(p: Profile, classDef: ClassDef): CompiledCharacter {
  const ch = p.characters[classDef.id];
  const loadout = ch?.loadouts.find((l) => l.id === ch.activeLoadoutId);
  // +specs: the chosen specialisation folds into the class before gear and talents.
  // +grid: the Waystone Grid compiles on top (sanitised: unknown / disconnected stars and overspend dropped).
  const grid = ch?.grid ? sanitizeGrid(classDef.id, ch.grid, gridPoints(p, classDef.id).total).nodes : [];
  return compileCharacter(withSpec(p, classDef), equippedGear(p, classDef.id), sanitizeTalents(classDef.id, loadout?.nodes ?? [], ch?.hero.talentPoints ?? 0), grid.length ? { grid } : {});
}

/**
 * The usable part of a saved talent list: unknown / other-class ids dropped (talent trees change
 * between builds), and if what is left breaks a gate or the point budget, the longest valid
 * prefix-in-order (fillLoadout). Never throws, so an old save always compiles.
 */
export function sanitizeTalents(classId: ClassId, nodes: readonly string[], points: number): string[] {
  const known = nodes.filter((id) => talentNode(id)?.classId === classId);
  const ok = validateLoadout({ id: '', name: '', classId, nodes: known }, points, 3).length === 0;
  return ok ? known : fillLoadout(classId, known, points, 3);
}

// ============================================================================
// Post-run update
// ============================================================================

export interface RunInput {
  result: RunResult;
  /** Slot order; the human (if any) is the entry with controller 'human'. */
  party: { classId: ClassId; controller: 'human' | 'bot' }[];
  botPreset: BotSkillPreset | null;
  seed: number;
  /**
   * Kill rewards summed from the run's death events (rewards.killRewardsFromEvents). When
   * omitted they are estimated from the RunResult (rewards.estimateKillRewards).
   */
  kills?: KillReward;
  /** Bosses killed (default: every required boss of a completed run, 0 otherwise). */
  bossesKilled?: number;
  /** World finds the run picked up (breakables, caches, eggs). Sanitised; never Sigils. */
  world?: ResourceBundle;
}

export interface ApplyRunOptions {
  /**
   * The caller's LOCAL date 'YYYY-MM-DD' (the web formats `new Date()`). Drives the Daily
   * Offering and the Weekly Ritual; omitted (tools, old tests) = no daily/weekly logic.
   * Progression never reads a clock.
   */
  localDate?: string;
}

export interface RunOutcome {
  profile: Profile;
  /**
   * @deprecated Item loot is gone (save v3): `items` is always empty, `bonus` null, `shards` 0.
   * `gems` still holds the run's gem drops (gem i belongs to party recipient i, human first).
   */
  loot: RunLoot;
  /** The Altar Offering: every resource line this run paid. */
  payout: RunPayout;
  record: RunRecord;
  ratingBefore: number;
  ratingAfter: number;
  standingBefore: RankStanding;
  standingAfter: RankStanding;
  /** Hero levels gained per class (only classes that levelled). */
  levelUps: Record<ClassId, number>;
  /** A vault offer was rolled by this run. */
  vaultReady: boolean;
  /** @deprecated always null since save v3 (see vaultAutoClaimedOffer). */
  vaultAutoClaimed: ItemId | null;
  /** The previous unclaimed vault's option auto-claimed because a new vault replaced it. */
  vaultAutoClaimedOffer: VaultOptionKind | null;
  /** XP (per party member) and gold this run paid, by source. */
  rewards: RunRewards;
}

/**
 * The pre-kill-reward flat hero XP for a run (kept as the tuning reference: rewards.ts keeps a
 * timed run at about this much). applyRunResult now uses rewards.runRewards.
 */
export function xpForRun(level: number, timed: boolean): number {
  return 60 + 10 * level + (timed ? 40 : 0);
}

/** Apply `xp` to a hero; returns levels gained. Points = level (leveling.addXp, capped at LEVEL_CAP). */
export function addHeroXp(ch: CharacterProgress, xp: number): number {
  return addXp(ch, xp);
}

/**
 * Fold a finished run into the profile (crafting.md §2): rating; the Altar Offering (resources
 * for the human's wallet, Fangsteel + Scales for every bot's satchel); gems; the Daily Offering
 * (first completed run of the caller's local day: banked Sigils, doubled Scales, Blessed
 * Charges); the first timed clear of the dungeon's bracket (Sigil + cache); Weekly Ritual
 * progress; hero XP and gold; the vault (a 1-of-3 resource offer every VAULT_EVERY runs); run
 * history. The tier BEFORE the run gates Eye fragments and gem ranks. Auto-managed bots socket
 * their own gems and re-pick talents (forging is autopilot's job).
 *
 * +field (v5): only the human's character is created when missing. Group Finder bots are not
 * stored (groupfinder.ts), so party members without a character get no XP, satchel or gems.
 *
 * RNG use (save contract): `rollRunResources` (gems, then the final boss's extra Eye fragment).
 * Nothing else draws, and nothing reads a clock: daily/weekly use `opts.localDate` only.
 */
export function applyRunResult(profile: Profile, run: RunInput, rng: Rng, opts: ApplyRunOptions = {}): RunOutcome {
  const p: Profile = structuredClone(profile);
  const r = run.result;
  const runIndex = p.runs.length + 1;
  const ratingBefore = totalRating(p.rating);
  const standingBefore = tierFor(ratingBefore);
  const tierBefore = standingBefore.tier;
  const localDate = opts.localDate !== undefined && isLocalDate(opts.localDate) ? opts.localDate : undefined;

  const humanSlot = run.party.find((m) => m.controller === 'human');
  if (humanSlot && !p.characters[humanSlot.classId]) p.characters[humanSlot.classId] = newCharacter(p, humanSlot.classId);

  // Rating
  p.rating[r.dungeonId] = updateRecord(p.rating[r.dungeonId], { dungeonId: r.dungeonId, level: r.level, timed: r.timed, elapsedMs: r.elapsedMs, timerMs: r.timerMs, runIndex });

  // The Offering (human first for gem ownership, stable slot order otherwise).
  const ordered = [...run.party].sort((a, b) => (a.controller === b.controller ? 0 : a.controller === 'human' ? -1 : 1));
  // +world-resources: the sim reports bosses killed on the result; callers may still override.
  const bosses = run.bossesKilled ?? r.bossesKilled ?? (r.completed ? dungeonBossCount(r.dungeonId) : 0);
  const res = rollRunResources(
    {
      level: r.level,
      completed: r.completed,
      timed: r.timed,
      chest: r.chest,
      bosses,
      finalBoss: r.completed,
      tier: tierBefore,
      recipients: ordered.length,
      gemsUnlocked: standingBefore.unlocks.includes('gems'),
    },
    rng,
  );
  // World finds were picked up during the run, so they count even on an abandoned run.
  // +world-resources: explicit `run.world` wins; otherwise the sim's own tally on the result.
  const world = sanitizeBundle(run.world ?? worldFindsToBundle(r.worldFinds, r.level));
  let daily: RunPayout['daily'] = null;
  const offering = r.completed ? dailyOffering(p.forge, localDate, res.base.scales) : null;
  if (offering) {
    daily = { sigils: offering.sigils, bonus: offering.bonus, favoredSlot: offering.favoredSlot };
    p.forge.daily = { lastClaimDay: offering.day };
  }
  let bracket: RunPayout['bracket'] = null;
  if (r.completed) {
    const b = bracketClear(p.forge, r.dungeonId, r.level, r.timed);
    if (b) {
      bracket = b;
      p.forge.brackets = { ...p.forge.brackets, [r.dungeonId]: sortGrades([...(p.forge.brackets[r.dungeonId] ?? []), b.grade]) };
    }
  }
  let weekly: RunPayout['weekly'] = null;
  if (localDate && r.completed) {
    p.forge.weekly = weeklyAfterRun(p.forge.weekly, localDate);
    weekly = { isoWeek: p.forge.weekly.isoWeek!, runs: p.forge.weekly.runs, ready: weeklyReady(p.forge.weekly, localDate) };
  }
  const total = addBundles(res.base, world, daily ? { sigils: daily.sigils } : null, daily?.bonus, bracket?.bundle);
  credit(p, total);
  const satchelTo = satchelRecipients(run.party);
  for (const cid of satchelTo) {
    const ch = p.characters[cid];
    if (!ch) continue; // a Group Finder bot: nobody to pay
    ch.satchel ??= emptySatchel();
    ch.satchel.fangsteel += res.satchel.fangsteel ?? 0;
    for (const g of SCALE_GRADES) ch.satchel.scales[g] += res.satchel.scales?.[g] ?? 0;
  }
  const payout: RunPayout = { grade: res.grade, base: res.base, world, daily, bracket, total, satchel: res.satchel, weekly };
  if (r.worldStats) payout.worldStats = { ...r.worldStats }; // +world-resources

  // Gems: gem i belongs to recipient i. A bot sockets its own gem if that improves its gear; the
  // rest (and every human gem) go to the shared stash.
  res.gems.forEach((g, i) => {
    const ch = p.characters[ordered[i]!.classId];
    if (ch?.autoManaged && socketForBot(p, ch, g)) return;
    p.gems = addGem(p.gems, g);
  });

  // Gold to the shared purse; hero XP + talents (the tier AFTER the run gates talent tiers)
  const rewards = runRewards(r, run.kills);
  p.currencies.gold = (p.currencies.gold ?? 0) + rewards.gold;
  const xp = rewards.xp;
  const maxTier = tierDef(tierFor(totalRating(p.rating)).tier).talentTiers;
  const levelUps: Record<ClassId, number> = {};
  for (const m of run.party) {
    const ch = p.characters[m.classId];
    if (!ch) continue;
    const gained = addHeroXp(ch, xp);
    if (gained > 0) levelUps[m.classId] = gained;
    if (ch.autoManaged) {
      const d = defaultLoadout(roleOf(m.classId), ch.hero.talentPoints, maxTier, m.classId);
      ch.loadouts = [d, ...ch.loadouts.filter((l) => l.id !== d.id)];
      ch.activeLoadoutId = d.id;
    }
  }

  // Vault: a 1-of-3 resource offer. A new vault never discards an unclaimed one: the old
  // offer's Scale Cache is credited first.
  const v = trackVault(p.vault, r.level, r.timed);
  p.vault = v.vault;
  let vaultAutoClaimedOffer: VaultOptionKind | null = null;
  if (v.ready) {
    const old = p.vault.offer;
    if (old) {
      const sc = old.options.find((o) => o.kind === 'scales');
      if (sc) {
        credit(p, vaultOptionBundle(sc));
        vaultAutoClaimedOffer = 'scales';
      }
    }
    p.vault.pending = null;
    p.vault.offer = vaultOffer(v.windowLevels, tierBefore);
  }

  const record: RunRecord = {
    runIndex,
    dungeonId: r.dungeonId,
    level: r.level,
    timed: r.timed,
    completed: r.completed,
    chest: r.chest,
    elapsedMs: r.elapsedMs,
    timerMs: r.timerMs,
    deaths: r.deaths,
    score: scoreRun(r),
    party: run.party.map((m) => ({ ...m })),
    botPreset: run.botPreset,
    seed: run.seed,
    loot: [],
    shards: 0,
    xp: rewards.xp,
    gold: rewards.gold,
    payout,
  };
  p.runs.push(record);
  const ratingAfter = totalRating(p.rating);
  return {
    profile: p,
    loot: { items: [], bonus: null, shards: 0, gems: res.gems },
    payout,
    record,
    ratingBefore,
    ratingAfter,
    standingBefore,
    standingAfter: tierFor(ratingAfter),
    levelUps,
    vaultReady: v.ready,
    vaultAutoClaimed: null,
    vaultAutoClaimedOffer,
    rewards,
  };
}

const sortGrades = (gs: ScaleGrade[]): ScaleGrade[] => [...new Set(gs)].sort((a, b) => gradeIndex(a) - gradeIndex(b));

function equippedLegendaryCount(p: Profile, ch: CharacterProgress, exceptSlot?: ItemSlot): number {
  let n = 0;
  for (const s of ITEM_SLOTS) if (s !== exceptSlot && itemById(p, ch.gear[s])?.legendaryId) n++;
  return n;
}

function canWear(p: Profile, ch: CharacterProgress, it: Item): boolean {
  if (it.legendaryId) {
    if (legendary(it.legendaryId)?.classId !== ch.classId) return false;
    if (equippedLegendaryCount(p, ch, it.slot) >= MAX_EQUIPPED_LEGENDARIES) return false;
  }
  return true;
}

/**
 * Bot auto-equip: wear `it` if it scores higher than the current piece. The old piece's gems
 * move onto the new one (socket order); any that do not fit go to the shared stash.
 */
function autoEquip(p: Profile, ch: CharacterProgress, it: Item): void {
  if (!canWear(p, ch, it)) return;
  const cur = itemById(p, ch.gear[it.slot]);
  if (cur && itemScore(it, ch.classId) <= itemScore(cur, ch.classId)) return;
  ch.gear[it.slot] = it.id;
  if (!cur) return;
  const moving = cur.gems.filter((g): g is Gem => g !== null);
  if (!moving.length) return;
  const gems = Array.from({ length: it.sockets }, (_, i) => it.gems[i] ?? null);
  for (let i = 0; i < gems.length && moving.length; i++) if (!gems[i]) gems[i] = moving.shift()!;
  for (const g of moving) p.gems = addGem(p.gems, g);
  replaceItem(p, { ...it, gems });
  replaceItem(p, { ...cur, gems: cur.gems.map(() => null) });
}

/** Put a bot's own gem into its gear if it improves it; true when used. */
function socketForBot(p: Profile, ch: CharacterProgress, g: Gem): boolean {
  if (g.color !== ROLE_GEM_COLOR[roleOf(ch.classId)]) return false;
  const res = autoGem(equippedGear(p, ch.classId), addGem({}, g), g.color, MAX_GEM_RANK);
  if (!res.changed) return false;
  for (const s of ITEM_SLOTS) if (res.gear[s]) replaceItem(p, res.gear[s]!);
  for (const [k, n] of Object.entries(res.stash)) if (n > 0) p.gems = { ...p.gems, [k]: (p.gems[k] ?? 0) + n };
  return true;
}

function replaceItem(p: Profile, it: Item): void {
  const i = p.inventory.findIndex((x) => x.id === it.id);
  if (i >= 0) p.inventory[i] = it;
}

/**
 * Claim vault option `index`: from save v3 a resource offer (Scale Cache / Vault Forge Token /
 * Altar Cache, crafting.md §2.4); a legacy item vault (old tools) adds the item to the bag.
 * Throws when there is nothing to claim at that index.
 */
export function claimVault(profile: Profile, index: number): Profile {
  const p = structuredClone(profile);
  const offer = p.vault.offer;
  if (offer) {
    const opt = offer.options[index];
    if (!opt) throw new Error(`no vault option ${index}`);
    credit(p, vaultOptionBundle(opt));
    if (opt.token) p.wallet.vaultTokens.push({ ...opt.token });
    p.vault.offer = null;
    return p;
  }
  const item = p.vault.pending?.options[index];
  if (!item) throw new Error(`no vault option ${index}`);
  p.inventory.push(item);
  p.vault.pending = null;
  return p;
}

/** True when a vault (offer or legacy item vault) is waiting to be claimed. */
export function vaultPending(p: Pick<Profile, 'vault'>): boolean {
  return !!p.vault.offer || !!p.vault.pending;
}

/**
 * Wear `itemId` on `classId` if it scores higher than the current piece (the old piece's gems
 * move over, extras go to the stash). Returns the profile unchanged when it is not an upgrade.
 */
export function wearIfBetter(profile: Profile, classId: ClassId, itemId: ItemId): Profile {
  const ch0 = profile.characters[classId];
  const it = itemById(profile, itemId);
  if (!ch0 || !it || itemOwner(profile, itemId)) return profile;
  const p = structuredClone(profile);
  autoEquip(p, p.characters[classId]!, itemById(p, itemId)!);
  return p;
}

// ============================================================================
// Gear operations (character sheet / upgrade / gems screens)
// ============================================================================

export type OpError =
  | 'unknownItem'
  | 'unknownClass'
  | 'wrongClass'
  | 'legendaryLimit'
  | 'equippedElsewhere'
  | 'equipped'
  | 'locked'
  | 'notEnoughShards'
  | 'notEnoughGold'
  | 'notAtCap'
  | 'maxMasterwork'
  | 'capped'
  | 'badTarget'
  | 'alreadyReforged'
  | 'duplicateStat'
  | 'badIndex'
  | 'badSocket'
  | 'noGem'
  | 'notEnough'
  | 'maxRank'
  | 'rankLocked'
  // Altar Forge (forge.ts)
  | 'cantAfford'
  | 'notTierSlot'
  | 'noLegendary'
  | 'reagentConflict'
  | 'noToken'
  | 'badReagent'
  | 'pendingRefine'
  | 'noRefine'
  | 'isLegendary'
  | 'alreadyTier'
  | 'notReady'
  | 'maxSockets'
  | 'maxGilding'
  | 'notTierPiece' // +forge-sinks
  | 'maxAttunement'; // +forge-sinks
export type OpResult = { ok: true; profile: Profile } | { ok: false; reason: OpError };

const fail = (reason: OpError): OpResult => ({ ok: false, reason });

/**
 * Equip an inventory item on a character. The item must not be worn by another character, a
 * legendary must be for this class, and only MAX_EQUIPPED_LEGENDARIES may be worn at once.
 */
export function equipItem(profile: Profile, classId: ClassId, itemId: ItemId): OpResult {
  const it = itemById(profile, itemId);
  if (!it) return fail('unknownItem');
  const ch0 = profile.characters[classId];
  if (!ch0) return fail('unknownClass');
  const owner = itemOwner(profile, itemId);
  if (owner && owner !== classId) return fail('equippedElsewhere');
  if (it.legendaryId && legendary(it.legendaryId)?.classId !== classId) return fail('wrongClass');
  if (it.legendaryId && equippedLegendaryCount(profile, ch0, it.slot) >= MAX_EQUIPPED_LEGENDARIES) return fail('legendaryLimit');
  const p = structuredClone(profile);
  p.characters[classId]!.gear[it.slot] = it.id;
  return { ok: true, profile: p };
}

export function unequipSlot(profile: Profile, classId: ClassId, slot: ItemSlot): OpResult {
  if (!profile.characters[classId]) return fail('unknownClass');
  const p = structuredClone(profile);
  delete p.characters[classId]!.gear[slot];
  return { ok: true, profile: p };
}

/** Bot vs player management of a character (auto-equip, auto-gem, recommended talents). */
export function setAutoManaged(profile: Profile, classId: ClassId, on: boolean): OpResult {
  if (!profile.characters[classId]) return fail('unknownClass');
  const p = structuredClone(profile);
  p.characters[classId]!.autoManaged = on;
  return { ok: true, profile: p };
}

/** Socket a gem from the stash (needs the gems unlock). A gem already there returns to the stash. */
export function socketGem(profile: Profile, itemId: ItemId, socket: number, gem: Gem): OpResult {
  if (!hasUnlock(profileRating(profile), 'gems')) return fail('locked');
  const it = itemById(profile, itemId);
  if (!it) return fail('unknownItem');
  const stash = takeGem(profile.gems, gem);
  if (!stash) return fail('noGem');
  const r = setSocket(it, socket, gem);
  if (!r.ok) return fail('badSocket');
  const p = structuredClone(profile);
  p.gems = r.removed ? addGem(stash, r.removed) : stash;
  replaceItem(p, r.item);
  return { ok: true, profile: p };
}

/** Take a gem out of a socket back into the stash (always allowed, free). */
export function unsocketGem(profile: Profile, itemId: ItemId, socket: number): OpResult {
  const it = itemById(profile, itemId);
  if (!it) return fail('unknownItem');
  const r = setSocket(it, socket, null);
  if (!r.ok) return fail('badSocket');
  const p = structuredClone(profile);
  if (r.removed) p.gems = addGem(p.gems, r.removed);
  replaceItem(p, r.item);
  return { ok: true, profile: p };
}

/** Fuse 3 stash gems into 1 of the next rank, up to the tier's gem rank cap. Costs fuseGoldCost(rank) gold. */
export function fuseGems(profile: Profile, color: GemColor, rank: number): OpResult {
  const max = profileMaxGemRank(profile);
  if (max === 0) return fail('locked');
  const r: FuseResult = fuse(profile.gems, color, rank, max);
  if (!r.ok) return fail(r.reason);
  const gold = fuseGoldCost(rank);
  if (profile.currencies.gold < gold) return fail('notEnoughGold');
  const p = structuredClone(profile);
  p.gems = r.stash;
  p.currencies.gold -= gold;
  return { ok: true, profile: p };
}

/**
 * LEGACY (save v2): spend Shards + gold to raise an item's level. Shards are always 0 from save
 * v3 on, so this now fails with 'notEnoughShards'; tempering (forge.temper) replaces it.
 */
export function upgradeItem(profile: Profile, itemId: ItemId, toIlvl: number): OpResult {
  const it = itemById(profile, itemId);
  if (!it) return fail('unknownItem');
  const r = upgrade(it, toIlvl, profile.currencies.shards, tierDef(profileStanding(profile).tier).maxIlvl, profile.currencies.gold);
  if (!r.ok) return fail(r.reason);
  const p = structuredClone(profile);
  p.currencies.shards -= r.cost;
  p.currencies.gold -= r.gold;
  replaceItem(p, r.item);
  return { ok: true, profile: p };
}

/**
 * Reforge one secondary (needs the reforge unlock): the deterministic stat-swap tool, costs
 * REFORGE_FANGSTEEL Fangsteel + REFORGE_GOLD gold (Shards are gone since save v3).
 */
export function reforgeItem(profile: Profile, itemId: ItemId, index: number, to: SecondaryStat): OpResult {
  if (!hasUnlock(profileRating(profile), 'reforge')) return fail('locked');
  const it = itemById(profile, itemId);
  if (!it) return fail('unknownItem');
  if (profile.wallet.fangsteel < REFORGE_FANGSTEEL) return fail('cantAfford');
  if (profile.currencies.gold < REFORGE_GOLD) return fail('notEnoughGold');
  const r = reforge(it, index, to);
  if (!r.ok) return fail(r.reason);
  const p = structuredClone(profile);
  p.wallet.fangsteel -= REFORGE_FANGSTEEL;
  p.currencies.gold -= REFORGE_GOLD;
  replaceItem(p, r.item);
  return { ok: true, profile: p };
}

/**
 * Bot gem setup for an auto-managed character: fill/upgrade sockets with the role colour from
 * the shared stash (the "recommended gem setup", PLAN §11). Call it from the UI when the
 * player asks for it; applyRunResult only socket bots' own drops.
 */
export function autoGemCharacter(profile: Profile, classId: ClassId): OpResult {
  const ch = profile.characters[classId];
  if (!ch) return fail('unknownClass');
  const max = profileMaxGemRank(profile);
  if (max === 0) return fail('locked');
  const res = autoGem(equippedGear(profile, classId), profile.gems, ROLE_GEM_COLOR[roleOf(classId)], max);
  const p = structuredClone(profile);
  p.gems = res.stash;
  for (const s of ITEM_SLOTS) if (res.gear[s]) replaceItem(p, res.gear[s]!);
  return { ok: true, profile: p };
}

// ============================================================================
// Save / load
// ============================================================================

export type Migration = (old: Record<string, unknown>) => Record<string, unknown>;

/** Migration from version N to N+1, keyed by N. Never edit a shipped migration; add a new one. */
export const MIGRATIONS: Readonly<Record<number, Migration>> = {
  // v0 → v1: pre-release saves had no settings block and no gem stash.
  0: (old) => ({
    ...old,
    gems: old['gems'] ?? {},
    settings: old['settings'] ?? { version: 1, defaultBotPreset: 'competent' },
    version: 1,
  }),
  // v1 → v2: gold. Existing saves start with an empty purse (one run pays a few hundred).
  1: (old) => {
    const cur = (old['currencies'] && typeof old['currencies'] === 'object' ? old['currencies'] : {}) as Record<string, unknown>;
    return { ...old, currencies: { ...cur, gold: typeof cur['gold'] === 'number' ? cur['gold'] : 0 }, version: 2 };
  },
  // v2 → v3: the Altar Forge (crafting.md §10).
  2: (old) => migrateV2toV3(old),
  // v3 → v4: the Guild Hall (docs/design/idle.md §8).
  3: (old) => migrateV3toV4(old),
  // v4 → v5: one character (docs/design/zones.md); the old save is kept whole under `legacy`.
  4: (old) => migrateV4toV5(old),
  // v5 → v6: the five base classes replace the four M+ kits (docs/design/classes/advancement.md).
  5: (old) => migrateV5toV6(old),
  // v6 → v7: Mossvale becomes one open zone (docs/design/zones.md "Mossvale").
  6: (old) => migrateV6toV7(old),
  // v7 → v8: specialisations (docs/design/classes/specs.md): every hero starts with none.
  7: (old) => migrateV7toV8(old),
  // v8 → v9: Mossvale v2, the 1–10 journey (docs/design/zones.md "Mossvale v2").
  8: (old) => migrateV8toV9(old),
  // v9 → v10: gathering professions (docs/design/professions.md): Foraging and Mining start at 1.
  9: (old) => migrateV9toV10(old), // +prof
  // v10 → v11: side quests (docs/design/zones.md "Side quests"): an empty quest log.
  10: (old) => migrateV10toV11(old), // +quests
  // v11 → v12: the main quest starts at Granny Pell's cottage (+spawn).
  11: (old) => migrateV11toV12(old),
  // v12 → v13: the Waystone Grid replaces talents (+grid, docs/design/waystone-grid.md).
  12: (old) => migrateV12toV13(old),
};

/**
 * +grid: save v12 → v13 (docs/design/waystone-grid.md). Pure function of the old JSON; never edit once
 * shipped. Every character gets an empty Waystone Grid (no stars, no seals open, no logged keys): its
 * points and most keys are derived from level and quests, so an old hero arrives with all of them to
 * spend. The retired talent loadouts are emptied (the base classes never had trees; old M+ kit nodes
 * stay in `legacy`).
 */
export function migrateV12toV13(old: Record<string, unknown>): Record<string, unknown> {
  const o = structuredClone(old) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const chars = o['characters'];
  if (chars && typeof chars === 'object') {
    for (const ch of Object.values(chars) as Record<string, any>[]) { // eslint-disable-line @typescript-eslint/no-explicit-any
      if (!ch || typeof ch !== 'object') continue;
      if (!ch['grid']) ch['grid'] = { nodes: [], locks: [], keyLog: [] };
      if (Array.isArray(ch['loadouts']) && PLAYABLE_CLASSES.includes(ch['classId'])) for (const l of ch['loadouts']) if (l && Array.isArray(l.nodes)) l.nodes = [];
    }
  }
  return { ...o, version: 13 };
}

/**
 * +spawn: the main quest gained "Tea with Granny Pell" (new step 0) and "The Road to Mossvale" moved
 * after the slimes. Old 0 (talk to the Elder; nothing done yet) → new 0 (Granny Pell first). Old 1
 * (slimes, the Elder already met) → new 1 (the slimes; the Elder's talk comes round again after
 * them, the one unavoidable repeat of the reorder). Old ≥ 2 → +1 (same step, past both).
 */
const chainV12 = (old: number): number => (old >= 2 ? old + 1 : old);

/**
 * +spawn: save v11 → v12 (docs/design/zones.md "The journey"). Pure function of the old JSON; never
 * edit once shipped. Positions, bounties, side quests and everything else stay; only Mossvale's
 * main-quest index is remapped onto the new chain (chainV12).
 */
export function migrateV11toV12(old: Record<string, unknown>): Record<string, unknown> {
  const o = structuredClone(old) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const chain = o['character']?.bounties?.chain;
  if (chain && typeof chain === 'object' && typeof chain['mossvale'] === 'number') chain['mossvale'] = chainV12(chain['mossvale']);
  return { ...o, version: 12 };
}

/**
 * +quests: save v10 → v11 (docs/design/zones.md "Side quests"). Pure function of the old JSON; never
 * edit once shipped. The character gets an empty side-quest log (nothing taken, nothing done).
 */
export function migrateV10toV11(old: Record<string, unknown>): Record<string, unknown> {
  const o = structuredClone(old) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const c = o['character'];
  if (c && typeof c === 'object' && !c.quests) c.quests = { active: {}, done: {} };
  return { ...o, version: 11 };
}

/**
 * +prof: save v9 → v10 (docs/design/professions.md). Pure function of the old JSON; never edit once
 * shipped. The character gets its profession state: Foraging 1, Mining 1, a full gather budget and
 * no gathers yet. Nothing else changes.
 */
export function migrateV9toV10(old: Record<string, unknown>): Record<string, unknown> {
  const o = structuredClone(old) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const c = o['character'];
  if (c && typeof c === 'object' && !c.prof) c.prof = { skills: { foraging: 1, mining: 1 }, nodeAt: {}, budget: { lastMs: null, tokens: 10 }, gathers: 0, byKind: {} };
  return { ...o, version: 10 };
}

/** +zone: v2's region → the waystone a hero who had discovered it gets attuned (frozen with the migration). */
const V9_REGION_WAYSTONES: Readonly<Record<string, string>> = {
  barrowfield: 'ws_barrow',
  glimmermere: 'ws_outpost',
  greyhorn: 'ws_climb',
  bramblewood: 'ws_bramble',
  pale_rise: 'ws_rise',
};
/**
 * +zone: the main quest gained "Up Goat's Climb" after "The Lake Road" (old index 8). A hero past the
 * lake road (old ≥ 9) keeps their place (+1); a hero on it stays on it.
 */
const chainV9 = (old: number): number => (old >= 9 ? old + 1 : old);

/**
 * Save v8 → v9 (docs/design/zones.md "Mossvale v2: the 1–10 journey"). Pure function of the old JSON;
 * never edit once shipped. The zone map grew 3× per side and every place moved, so a character
 * standing on it goes to town (in front of Elder Rowan); the main-quest index skips past the new
 * Greyhorn step for heroes already beyond it; heroes are attuned to the town waystone plus the
 * waystone of every region they had discovered. XP, gear, bounties and discoveries are untouched.
 */
export function migrateV8toV9(old: Record<string, unknown>): Record<string, unknown> {
  const o = structuredClone(old) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const c = o['character'];
  if (c && typeof c === 'object') {
    if (c.mapId === 'mossvale') {
      c.pos = { x: TOWN_POINT.pos.x, y: TOWN_POINT.pos.y }; // +spawn: the town square (where new heroes started then)
      c.facing = TOWN_POINT.facing;
    }
    const chain = c.bounties?.chain;
    if (chain && typeof chain === 'object' && typeof chain['mossvale'] === 'number') chain['mossvale'] = chainV9(chain['mossvale']);
    const ws = ['mossvale:ws_town'];
    for (const d of Array.isArray(c.discovered) ? c.discovered : []) {
      const id = typeof d === 'string' && d.startsWith('mossvale:') ? V9_REGION_WAYSTONES[d.slice(9)] : undefined;
      if (id && !ws.includes(`mossvale:${id}`)) ws.push(`mossvale:${id}`);
    }
    c.waystones = ws;
  }
  return { ...o, version: 9 };
}

/**
 * Save v7 → v8 (docs/design/classes/specs.md "Progression"). Pure function of the old JSON; never
 * edit once shipped. Every character's hero gets `specId: null` (chosen later at the Tower Warden);
 * nothing else changes.
 */
export function migrateV7toV8(old: Record<string, unknown>): Record<string, unknown> {
  const o = structuredClone(old) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const chars = (o['characters'] && typeof o['characters'] === 'object' ? o['characters'] : {}) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  for (const ch of Object.values(chars)) {
    if (ch && typeof ch === 'object' && ch.hero && typeof ch.hero === 'object' && ch.hero.specId === undefined) ch.hero.specId = null;
  }
  return { ...o, version: 8 };
}

/** +zone: the Phase 1 maps merged into the Mossvale zone (saves on them move to its town). */
const MERGED_MAPS: readonly string[] = ['mossvale_town', 'sunmeadow', 'bramblewood'];
/**
 * +zone: the zone chain gained two steps after the Hearthkeeper: "Bounties by the Well" (the board
 * unlock) and "The Lake Road". Old index 7 ("Into Bramblewood": past the Hearthkeeper, so the board
 * was already in use) skips the board step and lands on the lake road (new 8); later steps move up two.
 */
const chainV7 = (old: number): number => (old >= 8 ? old + 2 : old >= 7 ? old + 1 : old);

/**
 * Save v6 → v7 (docs/design/zones.md "Mossvale"). Pure function of the old JSON; never edit once
 * shipped. A character standing on one of the merged Phase 1 maps (town, Sunmeadow, Bramblewood)
 * moves to the zone map, in front of Elder Rowan; Mossvale's main-quest index is remapped past the
 * new board and lake steps (a hero past the Hearthkeeper keeps the board unlocked and takes the lake
 * road before Bramblewood). Bounties,
 * gear and XP are untouched.
 */
export function migrateV6toV7(old: Record<string, unknown>): Record<string, unknown> {
  const o = structuredClone(old) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const c = o['character'];
  if (c && typeof c === 'object') {
    if (typeof c.mapId === 'string' && MERGED_MAPS.includes(c.mapId)) {
      c.mapId = TOWN_POINT.mapId; // +spawn: the town square (where new heroes started then)
      c.pos = { x: TOWN_POINT.pos.x, y: TOWN_POINT.pos.y };
      c.facing = TOWN_POINT.facing;
    }
    const chain = c.bounties?.chain;
    if (chain && typeof chain === 'object' && typeof chain['mossvale'] === 'number') chain['mossvale'] = chainV7(chain['mossvale']);
  }
  return { ...o, version: 7 };
}

/**
 * Save v5 → v6 (docs/design/classes/advancement.md). Pure function of the old JSON; never edit
 * once shipped. The retired kits become their base class (warden → warrior, lightbinder → acolyte,
 * blademaster → rogue, stormcaller → mage): `mainClassId`, the character's class and every
 * `characters` entry are re-keyed, gear, level, XP and bags kept; talent loadouts are cleared (the
 * old trees are retired). `legacy` is untouched.
 */
export function migrateV5toV6(old: Record<string, unknown>): Record<string, unknown> {
  const o = structuredClone(old) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const to = (id: unknown): unknown => (typeof id === 'string' && RETIRED_CLASS_TO_BASE[id] ? RETIRED_CLASS_TO_BASE[id] : id);
  o['mainClassId'] = to(o['mainClassId']);
  if (o['character'] && typeof o['character'] === 'object') o['character'].classId = to(o['character'].classId);
  const chars = (o['characters'] && typeof o['characters'] === 'object' ? o['characters'] : {}) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const next: Record<string, unknown> = {};
  for (const [id, ch] of Object.entries(chars)) {
    const nid = to(id) as string;
    if (ch && typeof ch === 'object') {
      ch.classId = nid;
      for (const l of Array.isArray(ch.loadouts) ? ch.loadouts : []) {
        l.classId = nid;
        l.nodes = [];
      }
    }
    next[nid] = ch;
  }
  o['characters'] = next;
  return { ...o, version: 6 };
}

/**
 * Save v4 → v5 (docs/design/zones.md "Existing saves"). Pure function of the old JSON; never edit
 * once shipped. `legacy` = a deep copy of the entire v4 save (Guild Hall, characters, wallet, runs:
 * nothing is destroyed). The profile itself starts fresh: no character (`character: null`,
 * `characters: {}`), a new wallet / forge / inventory; the old main class is kept as `mainClassId`
 * (preselected on character creation), with the same `seed` and the old settings.
 */
export function migrateV4toV5(old: Record<string, unknown>): Record<string, unknown> {
  const legacy = structuredClone(old);
  const main = typeof old['mainClassId'] === 'string' && old['mainClassId'] ? old['mainClassId'] : (PROFILE_CLASSES[0] ?? 'warden');
  const seed = typeof old['seed'] === 'number' && Number.isFinite(old['seed']) ? old['seed'] : 0;
  const fresh = newProfile(main, seed);
  const settings = old['settings'] && typeof old['settings'] === 'object' && !Array.isArray(old['settings']) ? { ...fresh.settings, ...structuredClone(old['settings'] as Record<string, unknown>) } : fresh.settings;
  return { ...fresh, settings, legacy, version: 5 };
}

/** The Guild Hall block a v4 save carried (shape frozen with migrateV3toV4; the roster code is gone). */
function v4EmptyGuildHall(): Record<string, unknown> {
  return { v: 1, roster: [], rosterCap: 4, capHours: 12, jobs: [], nextSerial: 1, candies: 0, companions: {}, log: [], lastClaimMs: null };
}

/**
 * Save v3 → v4 (docs/design/idle.md §8). Pure function of the old JSON; never edit once shipped.
 * Adds an empty Guild Hall (like a new profile: you recruit from the keys you run). A veteran with
 * timed keys gets one welcome Hourglass. Nothing else changes.
 */
export function migrateV3toV4(old: Record<string, unknown>): Record<string, unknown> {
  const o = structuredClone(old);
  const idle = v4EmptyGuildHall();
  const runs = Array.isArray(o['runs']) ? (o['runs'] as { timed?: unknown }[]) : [];
  if (runs.some((r) => r?.timed === true)) idle['candies'] = 1;
  return { ...o, idle, version: 4 };
}

/** Welcome cache every migrated (v2) profile gets (crafting.md §10.6). */
export const MIGRATION_WELCOME: Readonly<ResourceBundle> = { sigils: 2, venom: 20, moltskin: 3, blessed: 3 };

/**
 * Save v2 → v3 (crafting.md §10). Pure function of the old JSON; never edit once shipped.
 *  1. Equipped items are kept exactly (ids, ilvl, rarity, stats, gems, masterwork, reforge) and
 *     get a zero forge state. Masterwork above the new rarity ceiling is grandfathered.
 *  2. Unequipped items: legendaries stay in the bag; everything else is salvaged (legacy
 *     formula: synthetic tempering investment), gems to the stash.
 *  3. Shards → Fangsteel 1:1 (shards = 0). Gold and gems unchanged.
 *  4. A pending item vault becomes 1 Vault Forge Token at the grade of its best option's ilvl.
 *  5. Bracket credit: every bracket at or below each dungeon's best timed key is marked cleared
 *     and its Sigil granted.
 *  6. Welcome cache (MIGRATION_WELCOME), Epic pity starts at EPIC_PITY_START, 5 Eye fragments
 *     at Champion+.
 *  7. Every character gets an empty satchel. RunRecord.loot ids may now point at salvaged items.
 */
export function migrateV2toV3(old: Record<string, unknown>): Record<string, unknown> {
  const o = structuredClone(old) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const characters = (o['characters'] && typeof o['characters'] === 'object' ? o['characters'] : {}) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const inventory: Item[] = Array.isArray(o['inventory']) ? o['inventory'] : [];
  const worn = new Set<string>();
  for (const ch of Object.values(characters)) for (const id of Object.values(ch?.gear ?? {})) if (typeof id === 'string') worn.add(id);
  const wallet = { fangsteel: 0, scales: zeroScales(), venom: 0, moltskin: 0, eyeFragments: 0, sigils: 0, blessed: 0, vaultTokens: [] as { grade: ScaleGrade }[] };
  const fake = { wallet, currencies: { shards: 0, gold: 0 } } as unknown as Profile;
  let gems: Record<string, number> = o['gems'] && typeof o['gems'] === 'object' ? { ...o['gems'] } : {};
  const kept: Item[] = [];
  let salvaged = 0;
  for (const it of inventory) {
    if (worn.has(it.id) || it.legendaryId) {
      kept.push({ ...it, forge: { ...emptyItemForge(), ...(it.forge ?? {}) } });
      continue;
    }
    credit(fake, legacySalvageYield(it));
    for (const g of salvageGems(it)) gems = addGem(gems, g);
    salvaged++;
  }
  const cur = (o['currencies'] && typeof o['currencies'] === 'object' ? o['currencies'] : {}) as Record<string, unknown>;
  const shards = typeof cur['shards'] === 'number' && cur['shards'] > 0 ? Math.floor(cur['shards']) : 0;
  wallet.fangsteel += shards;
  // Pending item vault → a Vault Forge Token.
  const vault = (o['vault'] && typeof o['vault'] === 'object' ? o['vault'] : {}) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const pendingOpts: Item[] = Array.isArray(vault['pending']?.options) ? vault['pending'].options : [];
  if (pendingOpts.length) wallet.vaultTokens.push({ grade: gradeForCraftIlvl(Math.max(...pendingOpts.map((x) => x.ilvl ?? 0))) });
  // Bracket credit.
  const rating = (o['rating'] && typeof o['rating'] === 'object' ? o['rating'] : {}) as Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  const brackets: Record<string, ScaleGrade[]> = {};
  let bracketSigils = 0;
  for (const [d, rec] of Object.entries(rating).sort(([a], [b]) => a.localeCompare(b))) {
    const best = rec?.best?.level;
    if (typeof best !== 'number') continue;
    const top = gradeIndex(gradeForKey(best));
    brackets[d] = SCALE_GRADES.slice(0, top + 1);
    bracketSigils += top + 1;
  }
  wallet.sigils += bracketSigils;
  const champion = tierFor(totalRating(rating as Profile['rating'])).tier;
  const eye = ['champion', 'paragon', 'eternal'].includes(champion) ? 5 : 0;
  credit(fake, { ...MIGRATION_WELCOME, eyeFragments: eye });
  const gained = addBundles({ fangsteel: wallet.fangsteel, scales: { ...wallet.scales }, venom: wallet.venom, moltskin: wallet.moltskin, eyeFragments: wallet.eyeFragments, sigils: wallet.sigils, blessed: wallet.blessed });
  const forge = newForgeState();
  forge.brackets = brackets;
  forge.migrationReport = { salvagedItems: salvaged, gained, bracketSigils };
  for (const ch of Object.values(characters)) if (ch && typeof ch === 'object') ch.satchel = emptySatchel();
  return {
    ...o,
    characters,
    inventory: kept,
    gems,
    // A purse without Shards is broken, not silently repaired (the structural check rejects it).
    currencies: typeof cur['shards'] === 'number' ? { ...cur, shards: 0 } : o['currencies'],
    vault: { runsInWindow: vault['runsInWindow'] ?? 0, windowLevels: vault['windowLevels'] ?? [], pending: null, offer: null },
    settings: { ...(o['settings'] ?? {}), shareSigils: null },
    wallet,
    forge,
    version: 3,
  };
}

/** Clear the one-time migration report (the web shows it once on the hub). */
export function dismissMigrationReport(profile: Profile): Profile {
  if (!profile.forge.migrationReport) return profile;
  const p = structuredClone(profile);
  p.forge.migrationReport = null;
  return p;
}

export function serialize(p: Profile): string {
  return JSON.stringify(p);
}

export class SaveError extends Error {}

export interface DeserializeOptions {
  /** Override the migration table (tests). */
  migrations?: Readonly<Record<number, Migration>>;
  /** Override the target version (tests). */
  targetVersion?: number;
}

/**
 * Parse a save, migrating forward to PROFILE_VERSION, then validate it. Throws SaveError on
 * bad JSON, saves from a newer build, missing migrations or a broken structure. Dangling
 * equipped-item references are repaired (unequipped) rather than rejected.
 */
export function deserialize(json: string, opts: DeserializeOptions = {}): Profile {
  const migrations = opts.migrations ?? MIGRATIONS;
  const target = opts.targetVersion ?? PROFILE_VERSION;
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    throw new SaveError('save is not valid JSON');
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new SaveError('save is not an object');
  let obj = data as Record<string, unknown>;
  const raw = obj['version'];
  let v = raw === undefined ? 0 : raw;
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 0) throw new SaveError(`bad save version ${String(raw)}`);
  if (v > target) throw new SaveError(`save version ${v} is newer than this build (${target})`);
  while (v < target) {
    // v4 → v5 starts a fresh profile; a broken v4 (or older) save is still rejected, not hidden in `legacy`.
    if (v === 4 && migrations === MIGRATIONS) {
      const errs = structuralErrors(obj);
      if (errs.length) throw new SaveError(`save is broken: ${errs.join('; ')}`);
    }
    const m = migrations[v];
    if (!m) throw new SaveError(`no migration from save version ${v}`);
    obj = m(obj);
    v += 1;
    obj['version'] = v;
  }
  const errs = structuralErrors(obj);
  if (errs.length) throw new SaveError(`save is broken: ${errs.join('; ')}`);
  const p = obj as unknown as Profile;
  repairProfile(p);
  return p;
}

function structuralErrors(o: Record<string, unknown>): string[] {
  const errs: string[] = [];
  const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
  if (typeof o['nextId'] !== 'number') errs.push('nextId');
  if (typeof o['seed'] !== 'number') errs.push('seed');
  if (typeof o['mainClassId'] !== 'string') errs.push('mainClassId');
  if (!isObj(o['characters'])) errs.push('characters');
  if (!Array.isArray(o['inventory'])) errs.push('inventory');
  if (!isObj(o['gems'])) errs.push('gems');
  if (!isObj(o['currencies']) || typeof (o['currencies'] as Record<string, unknown>)['shards'] !== 'number' || typeof (o['currencies'] as Record<string, unknown>)['gold'] !== 'number') errs.push('currencies');
  if (!isObj(o['vault'])) errs.push('vault');
  if (!Array.isArray(o['runs'])) errs.push('runs');
  if (!isObj(o['rating'])) errs.push('rating');
  if (!isObj(o['settings'])) errs.push('settings');
  const w = o['wallet'];
  if (!isObj(w) || typeof w['fangsteel'] !== 'number' || !isObj(w['scales']) || !Array.isArray(w['vaultTokens'])) errs.push('wallet');
  const f = o['forge'];
  if (!isObj(f) || typeof f['rollIndex'] !== 'number' || typeof f['sinceEpic'] !== 'number' || !isObj(f['brackets']) || !Array.isArray(f['codex'])) errs.push('forge');
  // +field (v5): the one character, when created.
  const h = o['character'];
  if (h !== undefined && h !== null && (!isObj(h) || typeof h['classId'] !== 'string' || typeof h['name'] !== 'string' || typeof h['mapId'] !== 'string')) errs.push('character');
  return errs;
}

/** Unequip references to items that no longer exist, and make nextId exceed every numeric id. */
function repairProfile(p: Profile): void {
  for (const ch of Object.values(p.characters)) {
    for (const l of ch.loadouts ?? []) l.nodes = sanitizeTalents(ch.classId, l.nodes, ch.hero?.talentPoints ?? 0);
  }
  const ids = new Set(p.inventory.map((i) => i.id));
  for (const ch of Object.values(p.characters)) for (const s of ITEM_SLOTS) if (ch.gear[s] && !ids.has(ch.gear[s]!)) delete ch.gear[s];
  let maxId = 0;
  for (const i of p.inventory) {
    const n = Number(/^\D*(\d+)$/.exec(i.id)?.[1]);
    if (Number.isFinite(n)) maxId = Math.max(maxId, n);
  }
  for (const o of p.vault.pending?.options ?? []) {
    const n = Number(/^\D*(\d+)$/.exec(o.id)?.[1]);
    if (Number.isFinite(n)) maxId = Math.max(maxId, n);
  }
  if (p.nextId <= maxId) p.nextId = maxId + 1;
  // Forge fields added after v3 shipped default in place (forward-compatible repair).
  const fresh = newForgeState();
  p.forge = { ...fresh, ...p.forge, daily: { ...fresh.daily, ...p.forge.daily }, weekly: { ...fresh.weekly, ...p.forge.weekly } };
  p.wallet = { ...starterWalletZero(), ...p.wallet, scales: { ...zeroScales(), ...p.wallet.scales } };
  p.vault.offer ??= null;
  // +field (v5): the one character and the legacy save default in place.
  p.character ??= null;
  p.legacy ??= null;
  const h = p.character;
  if (h) {
    const fresh = newHeroCharacter(h.classId, h.name, h.look ?? 0);
    const b = { ...fresh.bounties, ...h.bounties };
    p.character = {
      ...fresh,
      ...h,
      bounties: { chain: { ...fresh.bounties.chain, ...b.chain }, chainProgress: { ...fresh.bounties.chainProgress, ...b.chainProgress }, active: { ...b.active }, completed: { ...b.completed } },
      report: { ...fresh.report, ...h.report },
      dungeonClears: { ...h.dungeonClears },
      dungeonEnteredId: h.dungeonEnteredId ?? null,
    };
    if (!p.characters[h.classId]) addCharacterProgress(p, h.classId);
  }
  for (const ch of Object.values(p.characters)) {
    ch.satchel = { ...emptySatchel(), ...ch.satchel, scales: { ...zeroScales(), ...ch.satchel?.scales } };
    // +grid: a well-formed grid (ids / connectivity are sanitised at compile; the save keeps what was spent).
    ch.grid = { nodes: [...(ch.grid?.nodes ?? [])], locks: [...(ch.grid?.locks ?? [])], keyLog: [...(ch.grid?.keyLog ?? [])] };
  }
}

const starterWalletZero = () => ({ fangsteel: 0, scales: zeroScales(), venom: 0, moltskin: 0, eyeFragments: 0, sigils: 0, blessed: 0, vaultTokens: [] });

/** Consistency problems in a loaded/updated profile (empty when healthy). */
export function validateProfile(p: Profile): string[] {
  const errs: string[] = [];
  const ids = new Set<string>();
  for (const it of p.inventory) {
    if (ids.has(it.id)) errs.push(`duplicate item id ${it.id}`);
    ids.add(it.id);
    errs.push(...validateItem(it));
  }
  const worn = new Map<string, ClassId>();
  for (const [cid, ch] of Object.entries(p.characters)) {
    let legs = 0;
    for (const s of ITEM_SLOTS) {
      const id = ch.gear[s];
      if (!id) continue;
      const it = itemById(p, id);
      if (!it) errs.push(`${cid}: ${s} references missing item ${id}`);
      else if (it.slot !== s) errs.push(`${cid}: ${id} is a ${it.slot}, worn as ${s}`);
      if (it?.legendaryId) legs++;
      if (worn.has(id)) errs.push(`${id} worn by ${worn.get(id)} and ${cid}`);
      worn.set(id, cid);
    }
    if (legs > MAX_EQUIPPED_LEGENDARIES) errs.push(`${cid}: ${legs} legendaries equipped`);
  }
  if (p.currencies.shards < 0) errs.push('negative shards');
  if (!(p.currencies.gold >= 0)) errs.push('negative gold');
  for (const [k, n] of Object.entries(p.gems)) if (n < 0) errs.push(`negative gem count ${k}`);
  const w = p.wallet;
  for (const k of ['fangsteel', 'venom', 'moltskin', 'eyeFragments', 'sigils', 'blessed'] as const) if (!(w[k] >= 0) || !Number.isInteger(w[k])) errs.push(`bad wallet ${k}: ${w[k]}`);
  for (const g of SCALE_GRADES) if (!(w.scales[g] >= 0) || !Number.isInteger(w.scales[g])) errs.push(`bad wallet scales.${g}`);
  if (w.blessed > BLESSED_CAP) errs.push('blessed over cap');
  for (const [cid, ch] of Object.entries(p.characters)) {
    const s = ch.satchel;
    if (!s) continue;
    if (!(s.fangsteel >= 0) || !(s.venom >= 0) || !(s.moltskin >= 0)) errs.push(`${cid}: negative satchel`);
    for (const g of SCALE_GRADES) if (!(s.scales[g] >= 0)) errs.push(`${cid}: negative satchel scales.${g}`);
  }
  for (const it of p.inventory) if (it.setId && !(['helm', 'chest', 'legs', 'gloves', 'boots'] as string[]).includes(it.slot)) errs.push(`${it.id}: tier set on a ${it.slot}`);
  if (p.forge.rollIndex < 0 || p.forge.sinceEpic < 0 || p.forge.belowRare < 0 || p.forge.eyeMisses < 0) errs.push('bad forge counters');
  const h = p.character;
  if (h) {
    if (!p.characters[h.classId]) errs.push(`character ${h.name} has no progress for ${h.classId}`);
    if (h.classId !== p.mainClassId) errs.push(`character class ${h.classId} is not the main class ${p.mainClassId}`);
    if (!(h.report.budget >= 0)) errs.push('negative kill budget');
    for (const [id, n] of Object.entries(h.bounties.active)) if (!(n >= 0)) errs.push(`bad bounty progress ${id}`);
  }
  return errs;
}
